import { loadAuthContext, permissionsGranted } from '../auth/auth.middleware.js';
import type { AuthContext } from '../auth/auth.types.js';
import {
  registerImportEntity,
  type ImportColumn,
  type ImportContext,
  type ImportEntity,
  type MissingRef,
  type ParsedRow,
} from '../csv/csv.registry.js';
import { createParty, partyInputSchema } from '../parties/parties.service.js';
import { issuesToErrors } from '../parties/parties.import.js';
import { parseDateCell } from '../sales/sales.import.js';
import { createPayout, payoutInputSchema, voidPayout } from './payments.service.js';
import {
  approveOrder,
  assertApprovalLimit,
  orderInclude,
  orderInputSchema,
  saveOrderDraft,
  type OrderInput,
} from './purchases.service.js';

const amountRe = /^\d{1,15}(\.\d{1,4})?$/;
const clean = (v: string | undefined) => (v ?? '').replace(/,/g, '').trim();

async function authFor(ctx: ImportContext): Promise<AuthContext> {
  const auth = await loadAuthContext(ctx.tx, ctx.userId, ctx.companyId);
  if (!auth) throw new Error('User not found');
  return auth;
}
const findVendor = (ctx: ImportContext, name: string) =>
  ctx.tx.party.findFirst({ where: { companyId: ctx.companyId, name, isVendor: true } });
async function createVendorRef(ctx: ImportContext, ref: MissingRef) {
  const p = await createParty(ctx.tx, {
    companyId: ctx.companyId,
    userId: ctx.userId,
    needsReview: true,
    input: partyInputSchema.parse({ name: ref.value, isVendor: true }),
  });
  return p.id;
}
async function removeVendorRef(ctx: ImportContext, ref: { kind: string; id: number }) {
  try {
    await ctx.tx.party.delete({ where: { id: ref.id } });
    return null;
  } catch {
    return 'a vendor created by this import is now used elsewhere';
  }
}

// ───────────────────────── purchase orders ─────────────────────────

interface OrderRow {
  number: string | null;
  vendorName: string;
  categories: { lineIndex: number; name: string }[];
  input: Omit<OrderInput, 'partyId'>;
}

const orderColumns: ImportColumn[] = [
  { key: 'number', label: 'PO number', example: 'PO-2026-0001' },
  {
    key: 'vendor_name',
    label: 'Vendor name',
    required: true,
    example: 'Dell Uganda',
    aliases: ['vendor', 'supplier'],
  },
  {
    key: 'order_date',
    label: 'Order date',
    required: true,
    example: '15/03/2026',
    aliases: ['date'],
  },
  { key: 'expected_date', label: 'Expected delivery', example: '30/03/2026' },
  { key: 'currency', label: 'Currency', example: 'UGX' },
  { key: 'exchange_rate', label: 'Exchange rate', example: '' },
  { key: 'reference', label: 'Reference', example: '' },
  { key: 'notes', label: 'Notes', example: '' },
  { key: 'terms', label: 'Terms', example: '' },
  {
    key: 'line_description',
    label: 'Line description',
    required: true,
    example: 'Laptop',
    aliases: ['description', 'item'],
  },
  { key: 'category', label: 'Expense category', example: 'Office Supplies' },
  { key: 'quantity', label: 'Quantity', example: '2', aliases: ['qty'] },
  { key: 'unit', label: 'Unit', example: 'each' },
  {
    key: 'unit_price',
    label: 'Unit price',
    required: true,
    example: '3000000',
    aliases: ['price'],
  },
  { key: 'discount_percent', label: 'Discount %', example: '0' },
  { key: 'vat_code', label: 'VAT category', example: 'VAT_STD' },
];

function parseOrderGroup(
  rows: { rowNumber: number; raw: Record<string, string> }[],
): ParsedRow<OrderRow> {
  const errors: string[] = [];
  const first = rows[0]!;
  const raw = first.raw;
  for (const o of rows.slice(1))
    for (const f of ['vendor_name', 'order_date', 'expected_date', 'currency', 'reference'])
      if (
        (o.raw[f] ?? '').trim() !== '' &&
        (o.raw[f] ?? '').trim().toLowerCase() !== (raw[f] ?? '').trim().toLowerCase()
      )
        errors.push(`Row ${o.rowNumber}: ${f} differs from row ${first.rowNumber}`);
  const vendorName = raw.vendor_name?.trim() ?? '';
  if (!vendorName) errors.push('Vendor name: required');
  const issueDate = parseDateCell(raw.order_date, 'Order date', errors);
  const expectedDate = parseDateCell(raw.expected_date, 'Expected delivery', errors, false);
  const rate = clean(raw.exchange_rate);
  if (rate && !/^\d{1,11}(\.\d{1,8})?$/.test(rate)) errors.push('Exchange rate: enter a number');
  const lines: OrderInput['lines'] = [];
  const categories: OrderRow['categories'] = [];
  for (const r of rows) {
    const at = `Row ${r.rowNumber}`;
    const desc = r.raw.line_description?.trim();
    if (!desc) {
      errors.push(`${at}: Line description is required`);
      continue;
    }
    const qty = clean(r.raw.quantity) || '1';
    const price = clean(r.raw.unit_price);
    const disc = clean(r.raw.discount_percent) || '0';
    if (!/^\d{1,11}(\.\d{1,4})?$/.test(qty) || Number(qty) <= 0)
      errors.push(`${at}: Quantity must be a positive number`);
    if (!amountRe.test(price)) errors.push(`${at}: Unit price must be a number`);
    if (!/^\d{1,3}(\.\d{1,4})?$/.test(disc) || Number(disc) > 100)
      errors.push(`${at}: Discount % must be 0 to 100`);
    const cat = r.raw.category?.trim();
    if (cat) categories.push({ lineIndex: lines.length, name: cat });
    lines.push({
      description: desc,
      quantity: qty,
      unit: r.raw.unit?.trim() || null,
      unitPrice: price || '0',
      discountType: Number(disc) > 0 ? 'percent' : 'none',
      discountValue: disc,
      vatCode: r.raw.vat_code?.trim().toUpperCase() || 'VAT_STD',
    });
  }
  if (errors.length) return { ok: false, errors };
  const parsed = orderInputSchema.omit({ partyId: true }).safeParse({
    issueDate,
    expectedDate,
    currency: raw.currency?.trim().toUpperCase() || undefined,
    exchangeRate: rate || undefined,
    reference: raw.reference?.trim() || null,
    notes: raw.notes?.trim() || null,
    terms: raw.terms?.trim() || null,
    lines,
  });
  if (!parsed.success)
    return { ok: false, errors: issuesToErrors(parsed.error, { lines: 'Lines' }) };
  return {
    ok: true,
    value: { number: raw.number?.trim() || null, vendorName, categories, input: parsed.data },
  };
}

const orderEntity: ImportEntity<OrderRow> = {
  key: 'purchase-orders',
  label: 'Purchase Orders',
  importPermission: 'purchases.order.import',
  columns: orderColumns,
  groupKey: 'number',
  parseRow: () => ({ ok: false, errors: ['Grouped import expected'] }),
  parseGroup: parseOrderGroup,
  rowKey: (v) => (v.number ? `po:${v.number.toLowerCase()}` : null),
  async validate(ctx, v) {
    const errors: string[] = [];
    if (ctx.options.asApproved) {
      const auth = await authFor(ctx);
      if (!permissionsGranted(auth.permissions, 'purchases.order.approve'))
        errors.push('You do not have permission to import purchase orders as approved');
    }
    const rates = await ctx.tx.taxRate.findMany({
      where: { companyId: ctx.companyId, kind: 'vat', active: true },
      select: { code: true },
    });
    const codes = new Set(rates.map((r) => r.code));
    v.input.lines.forEach((l, i) => {
      if (!codes.has(l.vatCode))
        errors.push(`Line ${i + 1}: VAT category "${l.vatCode}" does not exist`);
    });
    for (const c of v.categories) {
      const cat = await ctx.tx.expenseCategory.findFirst({
        where: { companyId: ctx.companyId, name: c.name },
      });
      if (!cat) errors.push(`Line ${c.lineIndex + 1}: expense category "${c.name}" does not exist`);
    }
    if (v.number && ctx.options.duplicate === 'update') {
      const ex = await ctx.tx.purchaseOrder.findFirst({
        where: { companyId: ctx.companyId, number: v.number },
      });
      if (ex && ex.status !== 'draft')
        errors.push(`${v.number} is already issued and cannot be updated`);
    }
    return errors;
  },
  async findExisting(ctx, v) {
    if (!v.number) return null;
    const o = await ctx.tx.purchaseOrder.findFirst({
      where: { companyId: ctx.companyId, number: v.number },
      include: orderInclude,
    });
    return o ? { id: o.id, snapshot: null } : null;
  },
  async missingRefs(ctx, v) {
    return (await findVendor(ctx, v.vendorName)) ? [] : [{ kind: 'vendor', value: v.vendorName }];
  },
  createRef: createVendorRef,
  removeRef: removeVendorRef,
  async create(ctx, v, refs) {
    const auth = await authFor(ctx);
    const partyId =
      (await findVendor(ctx, v.vendorName))?.id ??
      refs.get(`vendor:${v.vendorName}`) ??
      ctx.options.missingRef.map?.[v.vendorName];
    if (!partyId) throw new Error(`Vendor "${v.vendorName}" could not be resolved`);
    const lines = v.input.lines.map((l) => ({ ...l }));
    for (const c of v.categories) {
      const cat = await ctx.tx.expenseCategory.findFirst({
        where: { companyId: ctx.companyId, name: c.name },
      });
      if (cat) lines[c.lineIndex]!.categoryId = cat.id;
    }
    const created = await saveOrderDraft(ctx.tx, { auth, input: { ...v.input, lines, partyId } });
    if (v.number)
      await ctx.tx.purchaseOrder.update({ where: { id: created.id }, data: { number: v.number } });
    if (ctx.options.asApproved) {
      assertApprovalLimit(auth, created.baseTotal);
      await approveOrder(ctx.tx, auth, created.id);
    }
    return { id: created.id };
  },
  async update(ctx, id, v, refs) {
    const auth = await authFor(ctx);
    const partyId = (await findVendor(ctx, v.vendorName))?.id ?? refs.get(`vendor:${v.vendorName}`);
    if (!partyId) throw new Error(`Vendor "${v.vendorName}" could not be resolved`);
    await saveOrderDraft(ctx.tx, { auth, id, input: { ...v.input, partyId } });
  },
  async restore() {
    /* updates only touch drafts and are not individually restorable */
  },
  async remove(ctx, id) {
    const o = await ctx.tx.purchaseOrder.findFirst({ where: { id, companyId: ctx.companyId } });
    if (!o) return null;
    if (o.status !== 'draft')
      return `${o.number ?? 'an order'} has been issued; cancel it instead of undoing the import`;
    await ctx.tx.purchaseOrderLine.deleteMany({ where: { orderId: id } });
    await ctx.tx.purchaseOrder.delete({ where: { id } });
    return null;
  },
};

// ───────────────────────── payout receipts ─────────────────────────

interface PayoutRow {
  payoutNo: string | null;
  vendorName: string;
  account: string;
  input: Omit<ReturnType<typeof payoutInputSchema.parse>, 'partyId' | 'financialAccountId'>;
  bills: { number: string; amount: string }[];
}

const payoutColumns: ImportColumn[] = [
  { key: 'payout_number', label: 'Payout number', example: 'PAY-2026-0001' },
  { key: 'vendor_name', label: 'Vendor name', required: true, example: 'Dell Uganda' },
  {
    key: 'payout_date',
    label: 'Payout date',
    required: true,
    example: '20/03/2026',
    aliases: ['date'],
  },
  { key: 'account', label: 'Paid from (account name)', required: true, example: 'Stanbic Main' },
  { key: 'amount', label: 'Amount paid', required: true, example: '5640000' },
  { key: 'wht_withheld', label: 'Tax withheld', example: '0' },
  { key: 'currency', label: 'Currency', example: 'UGX' },
  { key: 'exchange_rate', label: 'Exchange rate', example: '' },
  { key: 'reference', label: 'Bank / MoMo reference', example: '' },
  {
    key: 'bill_number',
    label: 'Bill paid (our number)',
    example: 'BILL-2026-0001',
    aliases: ['bill'],
  },
  {
    key: 'allocated_amount',
    label: 'Amount allocated to that bill',
    example: '5640000',
    aliases: ['allocated'],
  },
];

function parsePayoutGroup(
  rows: { rowNumber: number; raw: Record<string, string> }[],
): ParsedRow<PayoutRow> {
  const errors: string[] = [];
  const first = rows[0]!;
  const raw = first.raw;
  for (const o of rows.slice(1))
    for (const f of [
      'vendor_name',
      'payout_date',
      'account',
      'amount',
      'wht_withheld',
      'currency',
      'reference',
    ])
      if (
        (o.raw[f] ?? '').trim() !== '' &&
        (o.raw[f] ?? '').trim().toLowerCase() !== (raw[f] ?? '').trim().toLowerCase()
      )
        errors.push(`Row ${o.rowNumber}: ${f} differs from row ${first.rowNumber}`);
  const vendorName = raw.vendor_name?.trim() ?? '';
  if (!vendorName) errors.push('Vendor name: required');
  const account = raw.account?.trim() ?? '';
  if (!account) errors.push('Paid from: required');
  const date = parseDateCell(raw.payout_date, 'Payout date', errors);
  const amount = clean(raw.amount);
  if (!amountRe.test(amount) || Number(amount) <= 0)
    errors.push('Amount paid: enter a positive number');
  const wht = clean(raw.wht_withheld) || '0';
  if (!amountRe.test(wht)) errors.push('Tax withheld: enter a number');
  const bills: PayoutRow['bills'] = [];
  for (const r of rows) {
    const b = r.raw.bill_number?.trim();
    if (!b) continue;
    const a = clean(r.raw.allocated_amount);
    if (!amountRe.test(a) || Number(a) <= 0)
      errors.push(`Row ${r.rowNumber}: allocated amount for ${b} must be a positive number`);
    else bills.push({ number: b, amount: a });
  }
  if (bills.length === 0) errors.push('Name at least one bill and the amount allocated to it');
  if (errors.length) return { ok: false, errors };
  return {
    ok: true,
    value: {
      payoutNo: raw.payout_number?.trim() || null,
      vendorName,
      account,
      bills,
      input: {
        payoutDate: date!,
        currency: raw.currency?.trim().toUpperCase() || undefined,
        exchangeRate: clean(raw.exchange_rate) || undefined,
        amount,
        whtWithheld: wht,
        reference: raw.reference?.trim() || null,
        notes: null,
        allocations: [],
        autoAllocate: false,
      },
    },
  };
}

const payoutEntity: ImportEntity<PayoutRow> = {
  key: 'payouts',
  label: 'Payout Receipts',
  importPermission: 'purchases.payout.import',
  columns: payoutColumns,
  groupKey: 'payout_number',
  parseRow: () => ({ ok: false, errors: ['Grouped import expected'] }),
  parseGroup: parsePayoutGroup,
  rowKey: (v) => (v.payoutNo ? `payout:${v.payoutNo.toLowerCase()}` : null),
  async validate(ctx, v) {
    const errors: string[] = [];
    const acct = await ctx.tx.financialAccount.findFirst({
      where: { companyId: ctx.companyId, name: v.account, status: 'active' },
    });
    if (!acct) errors.push(`Paid from: no active account named "${v.account}"`);
    const vendor = await findVendor(ctx, v.vendorName);
    if (vendor) {
      for (const b of v.bills) {
        const bill = await ctx.tx.vendorBill.findFirst({
          where: { companyId: ctx.companyId, number: b.number },
        });
        if (!bill) errors.push(`Bill ${b.number} does not exist`);
        else if (bill.partyId !== vendor.id)
          errors.push(`Bill ${b.number} belongs to a different vendor`);
        else if (!['approved', 'partially_paid'].includes(bill.status))
          errors.push(`Bill ${b.number} is ${bill.status.replace('_', ' ')} and cannot be paid`);
        else if (Number(b.amount) > Number(bill.total) - Number(bill.amountPaid))
          errors.push(`Amount for ${b.number} is more than what is owed`);
      }
    }
    const total = v.bills.reduce((s, b) => s + Number(b.amount), 0);
    if (Math.abs(total - (Number(v.input.amount) + Number(v.input.whtWithheld))) > 0.00005)
      errors.push('Allocated amounts must equal the amount paid plus tax withheld');
    if (v.payoutNo && ctx.options.duplicate === 'update')
      if (
        await ctx.tx.payoutReceipt.findFirst({
          where: { companyId: ctx.companyId, payoutNo: v.payoutNo },
        })
      )
        errors.push(`${v.payoutNo} already exists and posted payouts cannot be updated`);
    return errors;
  },
  async findExisting(ctx, v) {
    if (!v.payoutNo) return null;
    const p = await ctx.tx.payoutReceipt.findFirst({
      where: { companyId: ctx.companyId, payoutNo: v.payoutNo },
    });
    return p ? { id: p.id, snapshot: null } : null;
  },
  async missingRefs(ctx, v) {
    return (await findVendor(ctx, v.vendorName)) ? [] : [{ kind: 'vendor', value: v.vendorName }];
  },
  createRef: createVendorRef,
  removeRef: removeVendorRef,
  async create(ctx, v, refs) {
    const auth = await authFor(ctx);
    const partyId =
      (await findVendor(ctx, v.vendorName))?.id ??
      refs.get(`vendor:${v.vendorName}`) ??
      ctx.options.missingRef.map?.[v.vendorName];
    if (!partyId) throw new Error(`Vendor "${v.vendorName}" could not be resolved`);
    const account = await ctx.tx.financialAccount.findFirstOrThrow({
      where: { companyId: ctx.companyId, name: v.account },
    });
    const allocations = [];
    for (const b of v.bills) {
      const bill = await ctx.tx.vendorBill.findFirstOrThrow({
        where: { companyId: ctx.companyId, number: b.number },
      });
      allocations.push({ targetType: 'bill' as const, targetId: bill.id, amount: b.amount });
    }
    const p = await createPayout(
      ctx.tx,
      auth,
      { ...v.input, partyId, financialAccountId: account.id, allocations },
      { payoutNo: v.payoutNo ?? undefined },
    );
    return { id: p.id };
  },
  async update() {
    throw new Error('Posted payouts cannot be updated by import');
  },
  async restore() {
    /* never updated */
  },
  /** Rolling back a posted payout voids it (a reversal); it is never deleted. */
  async remove(ctx, id) {
    const auth = await authFor(ctx);
    try {
      await voidPayout(ctx.tx, auth, id, 'Import rolled back');
      return null;
    } catch (e) {
      return e instanceof Error ? e.message : 'could not void the payout';
    }
  },
};

registerImportEntity(orderEntity);
registerImportEntity(payoutEntity);
